import React, { useMemo, useCallback } from 'react';

import _ from 'lodash';
import ArrayBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ArrayBuilder';
import { Icon, Button } from '@scale/scaleui';
import { InitialMessage, MessageRole } from '@scale/shared/types/chat';

import { BuilderInputTypes, ObjectSchema } from '@scale/shared/taskTemplate/types';
import type { TypeBuilderMapType } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemasType';

interface InitialMessagesBuilderProps {
  initialMessages: Array<InitialMessage>;
  setInitialMessages: (arg: Array<InitialMessage>) => void;
  typeBuilderMap: TypeBuilderMapType;
}

const InitialMessagesBuilder: React.FC<InitialMessagesBuilderProps> = ({
  initialMessages,
  setInitialMessages,
  typeBuilderMap,
}) => {
  const nextRole = useMemo(() => {
    // get role that is not last message role
    const lastMessageRole = _.last(initialMessages)?.role;
    return lastMessageRole === MessageRole.User ? MessageRole.Assistant : MessageRole.User;
  }, [initialMessages]);

  const addMessage = useCallback(() => {
    const newMessages = [...initialMessages];
    const valuePlaceholder = `message_${initialMessages.length + 1}`;

    const message: InitialMessage = {
      role: nextRole,
      content: nextRole === MessageRole.User ? valuePlaceholder : '',
    };
    if (nextRole === MessageRole.Assistant) {
      message.options = [{ id: 'model_1', content: valuePlaceholder }];
    }
    newMessages.push(message);
    setInitialMessages(newMessages);
  }, [initialMessages, nextRole, setInitialMessages]);
  return (
    <>
      <ArrayBuilder
        typeBuilderMap={typeBuilderMap}
        elements={initialMessages}
        setElements={setInitialMessages}
        getKey={message => message.content ?? ''}
        getTitle={message =>
          `${_.capitalize(message.role === MessageRole.User ? 'Prompt' : 'Response')}: ${
            message.content
          }`
        }
        getSchema={element => {
          const schema: ObjectSchema = {};
          if (element.role === MessageRole.User) {
            schema.content = {
              displayName: 'Content',
              required: true,
              inputType: BuilderInputTypes.String,
            };
            schema.userEditable = {
              displayName: 'Can Edit User Message (Prompt)',
              required: false,
              inputType: BuilderInputTypes.Boolean,
            };
            schema.referenceText = {
              displayName: 'Reference Text',
              required: false,
              inputType: BuilderInputTypes.String,
            };
            schema.referenceUrl = {
              displayName: 'Reference Text URL',
              required: false,
              inputType: BuilderInputTypes.String,
            };
            schema.promptAttachments = {
              displayName: 'Attachments',
              required: false,
              inputType: BuilderInputTypes.PromptAttachments,
            };
          }
          if (element.role === MessageRole.Assistant) {
            schema.options = {
              displayName: 'Preloaded Responses',
              required: false,
              inputType: BuilderInputTypes.ProvidedResponses,
            };
          }
          return schema;
        }}
        fullWidth
      />
      <div className="flex">
        <Button size="sm" onClick={() => addMessage()} variant="outline">
          <Icon icon={['fas', 'plus']} size="sm" variant="primary" />
          Add a new initial {nextRole === MessageRole.User ? 'prompt' : 'response'} message
        </Button>
      </div>
    </>
  );
};

export default InitialMessagesBuilder;
